import {
    archiveModelLabel,
    createArchiveCountRequestUrl,
    createArchiveNavigationUrl,
    nextGridMode,
    normalizePricePair,
    priceRangePercent,
    safeStoredGridMode,
} from './archive-filter-utils.mjs';

const storageKey = 'seluvia.archiveCatalog.gridMode';
const popstateEventName = 'seluvia:archive-catalog-popstate';
let popstateReady = false;
const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const setPanelInert = (panel, inert) => {
    panel.inert = inert;

    if (inert) {
        panel.setAttribute('inert', '');
    } else {
        panel.removeAttribute('inert');
    }
};

const readGridMode = () => {
    try {
        return safeStoredGridMode(window.localStorage.getItem(storageKey));
    } catch {
        return 'default';
    }
};

const storeGridMode = (mode) => {
    try {
        window.localStorage.setItem(storageKey, mode);
    } catch {
        // Storage can be unavailable in privacy modes; the visual toggle still works.
    }
};

const visibleFocusableElements = (container) => [...container.querySelectorAll(focusableSelector)]
    .filter((element) => element instanceof HTMLElement
        && element.getClientRects().length > 0
        && !element.closest('[inert], [hidden], details:not([open])'));

const captureFormState = (container) => [...container.querySelectorAll('input, select, textarea')]
    .map((control) => ({
        control,
        checked: control instanceof HTMLInputElement ? control.checked : undefined,
        value: control.value,
    }));

const restoreFormState = (snapshot) => {
    snapshot.forEach(({ control, checked, value }) => {
        if (!control?.isConnected) {
            return;
        }

        control.value = value;

        if (control instanceof HTMLInputElement && typeof checked === 'boolean') {
            control.checked = checked;
        }
    });
};

const decimalValue = (value, fallback) => {
    const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));

    return Number.isFinite(parsed) ? parsed : fallback;
};

const ensurePopstateNavigation = () => {
    if (popstateReady) {
        return;
    }

    popstateReady = true;
    window.addEventListener('popstate', () => {
        const root = document.querySelector('[data-archive-catalog]');

        root?.dispatchEvent(new CustomEvent(popstateEventName, {
            detail: { url: window.location.href },
        }));
    });
};

const restoreViewport = (scrollTop) => {
    const html = document.documentElement;
    const previousBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo({ top: scrollTop, left: window.scrollX, behavior: 'instant' });
    html.style.scrollBehavior = previousBehavior;
};

const appendedProductsMessage = (total) => {
    if (total <= 0) {
        return 'Wszystkie produkty są już widoczne.';
    }

    if (total === 1) {
        return 'Dodano kolejny produkt.';
    }

    return archiveModelLabel(total) === 'MODELE'
        ? `Dodano ${total} kolejne produkty.`
        : `Dodano ${total} kolejnych produktów.`;
};

export const initArchiveCatalog = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.archiveCatalogReady === 'true') {
        return null;
    }

    const form = root.querySelector('[data-archive-filter-form]');
    const trigger = root.querySelector('[data-archive-filter-toggle]');
    const panel = root.querySelector('[data-archive-filter-panel]');
    const dialog = root.querySelector('[data-archive-filter-dialog]');
    const scrim = root.querySelector('[data-archive-filter-scrim]');
    const closeButton = root.querySelector('[data-archive-filter-close]');
    const sort = root.querySelector('[data-archive-sort]');
    const gridToggle = root.querySelector('[data-archive-grid-toggle]');
    const status = root.querySelector('[data-archive-filter-status]');
    const priceFilter = root.querySelector('[data-archive-price-filter]');
    const applyButton = root.querySelector('[data-archive-filter-apply]');
    const liveTotalNodes = root.querySelectorAll('[data-archive-live-total]');
    const liveLabelNodes = root.querySelectorAll('[data-archive-live-label]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let gridMode = readGridMode();
    let closeTimer = 0;
    let focusTimer = 0;
    let openSnapshot = [];
    let applyingFilters = false;
    let countTimer = 0;
    let countController = null;
    let countSequence = 0;
    let currentLiveTotal = 0;
    let openLiveTotal = 0;
    let queueLiveCount = () => {};
    let navigationController = null;
    let navigationSequence = 0;
    let loadMoreController = null;
    const lifecycleController = new AbortController();

    if (!(form instanceof HTMLFormElement)
        || !(trigger instanceof HTMLButtonElement)
        || !(panel instanceof HTMLElement)
        || !(dialog instanceof HTMLElement)) {
        return null;
    }

    const countEndpoint = form.dataset.archiveCountEndpoint || '';
    const countContext = {
        taxonomy: form.dataset.archiveTaxonomy || '',
        termId: Number.parseInt(form.dataset.archiveTermId || '0', 10) || 0,
    };
    const numberFormatter = new Intl.NumberFormat('pl-PL');
    currentLiveTotal = Math.max(0, Number.parseInt(form.dataset.archiveInitialTotal || '0', 10) || 0);
    openLiveTotal = currentLiveTotal;

    const updateLiveCount = (value) => {
        currentLiveTotal = Math.max(0, Math.floor(Number(value) || 0));
        const formatted = numberFormatter.format(currentLiveTotal);
        const label = archiveModelLabel(currentLiveTotal);

        liveTotalNodes.forEach((node) => {
            node.textContent = formatted;
        });
        liveLabelNodes.forEach((node) => {
            node.textContent = label;
        });
    };

    const setCountBusy = (busy) => {
        root.classList.toggle('is-count-loading', busy);

        if (applyButton instanceof HTMLButtonElement) {
            if (busy) {
                applyButton.setAttribute('aria-busy', 'true');
            } else {
                applyButton.removeAttribute('aria-busy');
            }
        }
    };

    const cancelLiveCount = () => {
        window.clearTimeout(countTimer);
        countController?.abort();
        countController = null;
        countSequence += 1;
        setCountBusy(false);
    };

    const syncGroupAllState = (section) => {
        const allButton = section?.querySelector('[data-archive-clear-group]');

        if (!(allButton instanceof HTMLButtonElement)) {
            return;
        }

        const hasSelection = [...section.querySelectorAll('input[type="checkbox"]')]
            .some((input) => input.checked);
        allButton.setAttribute('aria-pressed', String(!hasSelection));
    };

    const syncAllGroups = () => {
        dialog.querySelectorAll('[data-filter-group]').forEach(syncGroupAllState);
    };

    const setupPriceFilter = () => {
        if (!(priceFilter instanceof HTMLElement)) {
            return null;
        }

        const minNumber = priceFilter.querySelector('[data-archive-price-number="min"]');
        const maxNumber = priceFilter.querySelector('[data-archive-price-number="max"]');
        const minRange = priceFilter.querySelector('[data-archive-price-range="min"]');
        const maxRange = priceFilter.querySelector('[data-archive-price-range="max"]');

        if (!(minNumber instanceof HTMLInputElement)
            || !(maxNumber instanceof HTMLInputElement)
            || !(minRange instanceof HTMLInputElement)
            || !(maxRange instanceof HTMLInputElement)) {
            return null;
        }

        const boundMin = decimalValue(priceFilter.dataset.boundMin, 0);
        const boundMax = Math.max(boundMin, decimalValue(priceFilter.dataset.boundMax, boundMin));
        const clamp = (value) => Math.min(boundMax, Math.max(boundMin, value));
        const currency = minNumber.nextElementSibling?.textContent?.trim() || 'zł';

        const sync = (source = '') => {
            let minimum = clamp(decimalValue(
                source === 'number-min' ? minNumber.value : minRange.value,
                boundMin,
            ));
            let maximum = clamp(decimalValue(
                source === 'number-max' ? maxNumber.value : maxRange.value,
                boundMax,
            ));

            if (minimum > maximum) {
                if (source.endsWith('min')) {
                    maximum = minimum;
                } else {
                    minimum = maximum;
                }
            }

            const minimumText = String(minimum);
            const maximumText = String(maximum);
            minNumber.value = minimumText;
            maxNumber.value = maximumText;
            minRange.value = minimumText;
            maxRange.value = maximumText;
            priceFilter.style.setProperty(
                '--archive-price-min-percent',
                `${priceRangePercent(minimum, boundMin, boundMax)}%`,
            );
            priceFilter.style.setProperty(
                '--archive-price-max-percent',
                `${priceRangePercent(maximum, boundMin, boundMax)}%`,
            );
            minRange.setAttribute('aria-valuetext', `${minimumText} ${currency}`);
            maxRange.setAttribute('aria-valuetext', `${maximumText} ${currency}`);
        };

        minRange.addEventListener('input', () => {
            sync('range-min');
            root.classList.add('has-pending-filters');
            queueLiveCount();
        });
        maxRange.addEventListener('input', () => {
            sync('range-max');
            root.classList.add('has-pending-filters');
            queueLiveCount();
        });
        minNumber.addEventListener('change', () => sync('number-min'));
        maxNumber.addEventListener('change', () => sync('number-max'));
        sync();

        return {
            boundMax,
            boundMin,
            maxNumber,
            minNumber,
            sync,
        };
    };

    const priceController = setupPriceFilter();

    const requestLiveCount = async () => {
        if (!countEndpoint) {
            return;
        }

        countController?.abort();
        countController = new AbortController();
        const sequence = ++countSequence;
        const entries = [...new FormData(form).entries()]
            .filter(([, value]) => typeof value === 'string');
        const url = createArchiveCountRequestUrl(
            countEndpoint,
            entries,
            countContext,
            window.location.href,
        );

        try {
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { Accept: 'application/json' },
                signal: countController.signal,
            });
            const payload = await response.json().catch(() => ({}));

            if (!response.ok || !Number.isFinite(Number(payload?.total))) {
                throw new Error(payload?.message || `HTTP ${response.status}`);
            }

            if (sequence !== countSequence) {
                return;
            }

            updateLiveCount(payload.total);

            if (status) {
                status.textContent = `Liczba pasujących modeli: ${currentLiveTotal}.`;
            }
        } catch (error) {
            if (error?.name !== 'AbortError' && sequence === countSequence && status) {
                status.textContent = 'Nie udało się teraz przeliczyć produktów. Nadal możesz zastosować filtry.';
            }
        } finally {
            if (sequence === countSequence) {
                setCountBusy(false);
            }
        }
    };

    queueLiveCount = () => {
        if (!countEndpoint) {
            return;
        }

        window.clearTimeout(countTimer);
        setCountBusy(true);
        countTimer = window.setTimeout(requestLiveCount, 220);
    };

    const unlockPage = () => {
        document.body.classList.remove('has-archive-filter-drawer-open');
        document.body.style.removeProperty('--seluvia-filter-scrollbar-width');
    };

    const lockPage = () => {
        document.body.style.setProperty(
            '--seluvia-filter-scrollbar-width',
            `${Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`,
        );
        document.body.classList.add('has-archive-filter-drawer-open');
    };

    const isOpen = () => root.classList.contains('is-filters-open');

    const setOpen = (open, {
        restoreFocus = false,
        restoreValues = true,
    } = {}) => {
        window.clearTimeout(closeTimer);
        window.clearTimeout(focusTimer);

        if (open) {
            if (isOpen()) {
                closeButton?.focus({ preventScroll: true });
                return;
            }

            openSnapshot = captureFormState(dialog);
            openLiveTotal = currentLiveTotal;
            applyingFilters = false;
            setPanelInert(panel, false);
            panel.setAttribute('aria-hidden', 'false');
            trigger.setAttribute('aria-expanded', 'true');
            lockPage();
            window.requestAnimationFrame(() => root.classList.add('is-filters-open'));
            focusTimer = window.setTimeout(
                () => closeButton?.focus({ preventScroll: true }),
                reducedMotion.matches ? 0 : 220,
            );
            document.dispatchEvent(new CustomEvent('seluvia:filters', { detail: { type: 'open' } }));
            return;
        }

        if (!isOpen() && panel.getAttribute('aria-hidden') === 'true') {
            return;
        }

        if (restoreValues && !applyingFilters) {
            cancelLiveCount();
            restoreFormState(openSnapshot);
            syncAllGroups();
            priceController?.sync();
            updateLiveCount(openLiveTotal);
            root.classList.remove('has-pending-filters');
        } else {
            cancelLiveCount();
        }

        root.classList.remove('is-filters-open');
        panel.setAttribute('aria-hidden', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        unlockPage();
        closeTimer = window.setTimeout(
            () => setPanelInert(panel, true),
            reducedMotion.matches ? 0 : 520,
        );

        if (restoreFocus) {
            trigger.focus({ preventScroll: true });
        }

        document.dispatchEvent(new CustomEvent('seluvia:filters', { detail: { type: 'close' } }));
    };

    const setGridMode = (mode) => {
        gridMode = safeStoredGridMode(mode);
        const relaxed = gridMode === 'relaxed';
        document.body.classList.toggle('is-archive-grid-relaxed', relaxed);

        if (gridToggle instanceof HTMLButtonElement) {
            const label = relaxed
                ? gridToggle.dataset.labelRelaxed
                : gridToggle.dataset.labelDefault;

            gridToggle.setAttribute('aria-pressed', String(relaxed));
            gridToggle.classList.toggle('is-active', relaxed);

            const screenReaderLabel = gridToggle.querySelector('.screen-reader-text');

            if (screenReaderLabel && label) {
                screenReaderLabel.textContent = label;
            }
        }
    };

    const resultsWrapper = root.closest('[data-archive-results]');
    const setNavigationBusy = (busy) => {
        root.classList.toggle('is-loading', busy);
        resultsWrapper?.classList.toggle('is-loading', busy);
        form.toggleAttribute('aria-busy', busy);

        if (applyButton instanceof HTMLButtonElement) {
            applyButton.disabled = busy;
        }

        if (sort instanceof HTMLSelectElement) {
            sort.disabled = busy;
        }
    };
    const enablePriceInputs = () => {
        [...form.querySelectorAll('input:disabled[name="min_price"], input:disabled[name="max_price"]')]
            .forEach((input) => {
                input.disabled = false;
            });
    };
    const updateArchive = async (url, {
        focusSelector = '',
        historyMode = 'push',
    } = {}) => {
        if (!(resultsWrapper instanceof HTMLElement)) {
            window.location.assign(url);
            return;
        }

        const targetUrl = new URL(url, window.location.href);

        if (targetUrl.origin !== window.location.origin) {
            window.location.assign(targetUrl.toString());
            return;
        }

        navigationController?.abort();
        loadMoreController?.abort();
        loadMoreController = null;
        navigationController = new AbortController();
        const sequence = ++navigationSequence;
        const scrollTop = window.scrollY;
        applyingFilters = true;
        cancelLiveCount();

        if (isOpen()) {
            setOpen(false, { restoreFocus: false, restoreValues: false });
        }

        setNavigationBusy(true);

        if (status) {
            status.textContent = 'Aktualizuję listę produktów.';
        }

        try {
            const response = await fetch(targetUrl.toString(), {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: {
                    Accept: 'text/html',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: navigationController.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const responseDocument = new DOMParser().parseFromString(
                await response.text(),
                'text/html',
            );
            const nextResults = responseDocument.querySelector('[data-archive-results]');
            const nextRoot = nextResults?.querySelector('[data-archive-catalog]');

            if (!(nextResults instanceof HTMLElement) || !(nextRoot instanceof HTMLElement)) {
                throw new Error('Brak fragmentu katalogu w odpowiedzi.');
            }

            if (sequence !== navigationSequence) {
                return;
            }

            const importedResults = document.importNode(nextResults, true);
            const importedRoot = importedResults.querySelector('[data-archive-catalog]');

            if (!(importedRoot instanceof HTMLElement)) {
                throw new Error('Nie udało się przygotować nowego katalogu.');
            }

            lifecycleController.abort();
            resultsWrapper.replaceWith(importedResults);

            if (responseDocument.title) {
                document.title = responseDocument.title;
            }

            const resolvedUrl = response.url || targetUrl.toString();

            if (historyMode === 'push') {
                window.history.pushState({ seluviaArchiveCatalog: true }, '', resolvedUrl);
            } else if (historyMode === 'replace') {
                window.history.replaceState({ seluviaArchiveCatalog: true }, '', resolvedUrl);
            }

            restoreViewport(scrollTop);
            initArchiveCatalog(importedRoot);

            const nextFocusTarget = focusSelector
                ? importedRoot.querySelector(focusSelector)
                : null;

            nextFocusTarget?.focus({ preventScroll: true });

            const nextStatus = importedRoot.querySelector('[data-archive-filter-status]');

            if (nextStatus) {
                nextStatus.textContent = 'Lista produktów została zaktualizowana.';
            }

            window.requestAnimationFrame(() => {
                restoreViewport(scrollTop);
                window.requestAnimationFrame(() => restoreViewport(scrollTop));
            });
            document.dispatchEvent(new CustomEvent('seluvia:archive-catalog-updated', {
                detail: { url: resolvedUrl },
            }));
        } catch (error) {
            if (error?.name === 'AbortError' || sequence !== navigationSequence) {
                return;
            }

            applyingFilters = false;
            enablePriceInputs();
            setNavigationBusy(false);

            if (status) {
                status.textContent = 'Nie udało się odświeżyć produktów. Spróbuj ponownie.';
            }
        } finally {
            if (sequence === navigationSequence) {
                navigationController = null;
            }
        }
    };
    const loadNextProducts = async (button) => {
        const productGrid = resultsWrapper?.querySelector('ul.products');
        const control = button.closest('[data-archive-load-more]');

        if (!(button instanceof HTMLAnchorElement)
            || !(productGrid instanceof HTMLElement)
            || !(control instanceof HTMLElement)
            || button.getAttribute('aria-busy') === 'true') {
            return;
        }

        const targetUrl = new URL(button.href, window.location.href);

        if (targetUrl.origin !== window.location.origin) {
            window.location.assign(targetUrl.toString());
            return;
        }

        loadMoreController?.abort();
        loadMoreController = new AbortController();
        const controller = loadMoreController;
        const label = button.querySelector('[data-archive-load-more-label]');
        const originalLabel = label?.textContent || 'POKAŻ WIĘCEJ';
        const scrollTop = window.scrollY;
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');

        if (label) {
            label.textContent = 'ŁADOWANIE';
        }

        if (status) {
            status.textContent = 'Wczytuję kolejne produkty.';
        }

        try {
            const response = await fetch(targetUrl.toString(), {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: {
                    Accept: 'text/html',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const responseDocument = new DOMParser().parseFromString(
                await response.text(),
                'text/html',
            );
            const nextResults = responseDocument.querySelector('[data-archive-results]');
            const nextGrid = nextResults?.querySelector('ul.products');
            const nextControl = nextResults?.querySelector('[data-archive-load-more]');

            if (!(nextGrid instanceof HTMLElement)) {
                throw new Error('Brak kolejnej strony produktów w odpowiedzi.');
            }

            const existingKeys = new Set(
                [...productGrid.querySelectorAll('[data-product-card]')]
                    .map((card) => card.dataset.productId
                        || card.querySelector('a[href]')?.href
                        || '')
                    .filter(Boolean),
            );
            const appendedCards = [];

            [...nextGrid.querySelectorAll(':scope > [data-product-card]')].forEach((card) => {
                const key = card.dataset.productId || card.querySelector('a[href]')?.href || '';

                if (key && existingKeys.has(key)) {
                    return;
                }

                if (key) {
                    existingKeys.add(key);
                }

                const importedCard = document.importNode(card, true);
                importedCard.classList.add('is-archive-appended');
                appendedCards.push(importedCard);
            });

            if (appendedCards.length > 0) {
                productGrid.append(...appendedCards);
            }

            let nextButton = null;

            if (nextControl instanceof HTMLElement && appendedCards.length > 0) {
                const importedControl = document.importNode(nextControl, true);
                control.replaceWith(importedControl);
                nextButton = importedControl.querySelector('[data-archive-load-more-button]');

                if (productGrid.id) {
                    nextButton?.setAttribute('aria-controls', productGrid.id);
                }
            } else {
                control.remove();
            }

            restoreViewport(scrollTop);
            nextButton?.focus({ preventScroll: true });
            window.requestAnimationFrame(() => {
                appendedCards.forEach((card) => card.classList.add('is-visible'));
                restoreViewport(scrollTop);
            });

            if (status) {
                status.textContent = appendedProductsMessage(appendedCards.length);
            }

            document.dispatchEvent(new CustomEvent('seluvia:archive-products-appended', {
                detail: {
                    count: appendedCards.length,
                    page: Number.parseInt(button.dataset.nextPage || '0', 10) || null,
                    url: response.url || targetUrl.toString(),
                },
            }));
        } catch (error) {
            if (error?.name === 'AbortError') {
                return;
            }

            button.removeAttribute('aria-busy');
            button.removeAttribute('aria-disabled');

            if (label) {
                label.textContent = originalLabel;
            }

            if (status) {
                status.textContent = 'Nie udało się wczytać kolejnych produktów. Spróbuj ponownie.';
            }
        } finally {
            if (loadMoreController === controller) {
                loadMoreController = null;
            }
        }
    };

    root.classList.add('is-ready');
    setPanelInert(panel, true);
    panel.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    syncAllGroups();
    setGridMode(gridMode);
    ensurePopstateNavigation();

    const productGrid = resultsWrapper?.querySelector('ul.products');
    const loadMoreButton = resultsWrapper?.querySelector('[data-archive-load-more-button]');

    if (productGrid instanceof HTMLElement) {
        productGrid.id ||= 'archive-product-grid';
        loadMoreButton?.setAttribute('aria-controls', productGrid.id);
    }

    if (window.location.hash === `#${dialog.id}`) {
        setOpen(true);
    }

    trigger.addEventListener('click', () => {
        setOpen(!isOpen(), { restoreFocus: isOpen() });
    });
    scrim?.addEventListener('click', () => setOpen(false, { restoreFocus: true }));
    closeButton?.addEventListener('click', () => setOpen(false, { restoreFocus: true }));

    dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false, { restoreFocus: true });
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusable = visibleFocusableElements(dialog);
        const first = focusable[0];
        const last = focusable.at(-1);

        if (!first || !last) {
            event.preventDefault();
            dialog.focus();
        } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    });

    dialog.querySelectorAll('[data-archive-clear-group]').forEach((button) => {
        button.addEventListener('click', () => {
            const section = button.closest('[data-filter-group]');

            section?.querySelectorAll('input[type="checkbox"]')
                .forEach((input) => {
                    input.checked = false;
                });
            syncGroupAllState(section);
            root.classList.add('has-pending-filters');
            queueLiveCount();
        });
    });

    sort?.addEventListener('change', () => {
        if (status) {
            status.textContent = 'Aktualizuję kolejność produktów.';
        }

        form.requestSubmit();
    });

    gridToggle?.addEventListener('click', () => {
        setGridMode(nextGridMode(gridMode));
        storeGridMode(gridMode);
    });

    form.addEventListener('change', (event) => {
        if (event.target !== sort) {
            root.classList.add('has-pending-filters');
            syncGroupAllState(event.target?.closest?.('[data-filter-group]'));
            queueLiveCount();
        }
    });

    form.addEventListener('submit', (event) => {
        if (!(resultsWrapper instanceof HTMLElement)) {
            return;
        }

        event.preventDefault();
        cancelLiveCount();
        applyingFilters = true;
        const minInput = form.elements.namedItem('min_price');
        const maxInput = form.elements.namedItem('max_price');

        if (minInput instanceof HTMLInputElement && maxInput instanceof HTMLInputElement) {
            const [minimum, maximum] = normalizePricePair(minInput.value, maxInput.value);
            minInput.value = minimum === null ? '' : String(minimum);
            maxInput.value = maximum === null ? '' : String(maximum);

            minInput.disabled = minimum === null
                || (priceController && minimum <= priceController.boundMin);
            maxInput.disabled = maximum === null
                || (priceController && maximum >= priceController.boundMax);
        }

        const entries = [...new FormData(form).entries()]
            .filter(([, value]) => typeof value === 'string');
        const url = createArchiveNavigationUrl(form.action, entries, window.location.href);
        const focusSelector = event.submitter === applyButton
            ? '[data-archive-filter-toggle]'
            : '[data-archive-sort]';

        void updateArchive(url, { focusSelector });
    });

    root.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)
            || event.defaultPrevented
            || event.button !== 0
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey) {
            return;
        }

        const link = event.target.closest('a[data-archive-filter-navigation]');

        if (!(link instanceof HTMLAnchorElement)) {
            return;
        }

        event.preventDefault();
        void updateArchive(link.href, { focusSelector: '[data-archive-filter-toggle]' });
    });

    resultsWrapper?.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)
            || event.defaultPrevented
            || event.button !== 0
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey) {
            return;
        }

        const button = event.target.closest('[data-archive-load-more-button]');

        if (!(button instanceof HTMLAnchorElement)) {
            return;
        }

        event.preventDefault();
        void loadNextProducts(button);
    });

    root.addEventListener(popstateEventName, (event) => {
        const url = event.detail?.url;

        if (url) {
            void updateArchive(url, { historyMode: 'none' });
        }
    });

    window.addEventListener('pageshow', (event) => {
        setNavigationBusy(false);
        applyingFilters = false;
        cancelLiveCount();
        enablePriceInputs();

        if (event.persisted) {
            setOpen(false, { restoreFocus: false, restoreValues: false });
        }
    }, { signal: lifecycleController.signal });

    root.dataset.archiveCatalogReady = 'true';
    updateLiveCount(currentLiveTotal);

    return {
        close: (options) => setOpen(false, options),
        navigate: (url, options) => updateArchive(url, options),
        open: () => setOpen(true),
    };
};
